import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Scale } from "lucide-react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/SeoHead";
import { POSTS, readingTime } from "@/content/blog/posts.mjs";

const fmt = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });

function PostCard({ post }) {
  return (
    <Link
      to={post.path}
      data-testid={`blog-card-${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 transition-colors hover:border-accent/50"
    >
      <div className="overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          loading="lazy"
          className="aspect-[3/2] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent">{post.category}</p>
        <h3 className="mt-3 text-lg font-semibold leading-snug tracking-tight group-hover:text-accent">
          {post.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{post.description}</p>
        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {fmt(post.date)} · {readingTime(post)} min read
        </p>
      </div>
    </Link>
  );
}

export default function Blog() {
  const guides = POSTS.filter((p) => p.kind === "guide");
  const comparisons = POSTS.filter((p) => p.kind === "comparison");
  const pillar = POSTS.find((p) => p.kind === "pillar");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Sema Blog",
    url: "https://semalayer.com/blog",
    description: "Practical guides on semantic layers, text-to-SQL governance and AI data governance.",
  };

  return (
    <div data-testid="blog-root" className="relative min-h-screen w-full bg-background text-foreground">
      <SeoHead
        title="Blog — Semantic Layer, Text-to-SQL Governance & AI Analytics | Sema"
        description="Practical guides on semantic layers, text-to-SQL governance, AI data governance and trustworthy analytics — from the team building Sema."
        keywords="semantic layer blog, text-to-sql governance, ai data governance"
        path="/blog"
        jsonLd={jsonLd}
      />
      <MarketingNav />

      <section className="relative overflow-hidden pt-36 pb-10 sm:pt-40">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{ background: "radial-gradient(55rem 28rem at 50% -10%, #0FAE96, transparent)" }}
        />
        <div className="relative mx-auto max-w-6xl px-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">Blog</p>
          <h1 data-testid="blog-h1" className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
            Notes on trustworthy AI analytics.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Practical writing on semantic layers, governed text-to-SQL, and the messy reality of making AI safe on
            enterprise data.
          </p>
        </div>
      </section>

      {pillar && (
        <section className="py-6">
          <div className="mx-auto max-w-6xl px-6">
            <Link
              to={pillar.path}
              data-testid="blog-pillar-link"
              className="group flex flex-col gap-4 rounded-2xl border border-accent/40 bg-accent/5 p-8 transition-colors hover:border-accent sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent">
                  <BookOpen className="mr-2 inline h-3.5 w-3.5" />
                  The pillar guide
                </p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight">{pillar.title}</h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{pillar.description}</p>
              </div>
              <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-accent">
                Read the guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </section>
      )}

      <section className="py-10">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Guides</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {guides.map((p) => (
              <PostCard key={p.slug} post={p} />
            ))}
          </div>
        </div>
      </section>

      <section id="comparisons" className="py-10 pb-24">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Scale className="mr-2 inline h-3.5 w-3.5" />
            Comparisons
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {comparisons.map((p) => (
              <PostCard key={p.slug} post={p} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
